'use client';

import { useFetchScratchcards, useGamesList } from '@shared/hooks';
import { ROUTES, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { useGlobalSheet } from '../../../global-sheets/context/GlobalSheetContext';
import { processMultipleOfferData, shouldShowOfferExpirationDate } from '../../helpers/offerHelpers';
import { useOfferBundleActionHandlers } from '../../hooks/useOfferBundleActionHandlers';
import type { OfferBundleDetails } from '../../types';
import { getStepCode, shouldShowLoginButtons } from '../../utils/offerUtils';
import { OfferContent } from '../OfferContent/OfferContent';
import { OfferHeader } from '../OfferHeader/OfferHeader';
import { OfferStickyPanelButtons } from '../OfferStickyPanelButtons/OfferStickyPanelButtons';

type MultipleOfferProps = {
  isAuthenticated: boolean;
  className?: string;
  data: OfferBundleDetails;
  refetchCallback?: () => Promise<void>;
} & Record<`data-track-${string}`, string | number | undefined>;

function MultipleOffer({ isAuthenticated, className, data, refetchCallback, ...trackingAttributes }: MultipleOfferProps) {
  const t = useTranslations();
  const currencyFormatter = useCurrencyFormatter();
  const { games } = useGamesList();
  const { scratchcards } = useFetchScratchcards({});
  const { promotionUnlockStep, offerGroups, mainStepAction, mainStepButtonText, hasActiveMainStep } = processMultipleOfferData(
    data,
    isAuthenticated,
    t,
    currencyFormatter,
    games,
    scratchcards
  );
  const { openDepositSheet } = useGlobalSheet();
  const actionHandlers = useOfferBundleActionHandlers({ refetch: refetchCallback, openDepositSheet, scratchcards });
  const showLoginButtons = shouldShowLoginButtons(isAuthenticated, data);
  const termsTitle = t('offers.page.terms.title');
  const bundleValidFrom = data.validFromDate ? new Date(data.validFromDate) : undefined;

  const mainStepActionCallback = useCallback(() => {
    if (hasActiveMainStep) {
      let minimumAmountForBonus: number | undefined = undefined;

      if (mainStepAction === 'DEPOSIT' && data.step?.action === 'DEPOSIT' && data.step?.initialAmount?.fixed_amount) {
        minimumAmountForBonus = data.step.initialAmount.fixed_amount;
      }

      const handlerData = {
        bundleCode: data.bundleCode,
        minimumAmountForBonus,
        redirectUrlOnInsufficientDeposit: ROUTES.HOME,
        productURL: data.step?.productURL,
      };

      const handler = actionHandlers[promotionUnlockStep.cta.action as keyof typeof actionHandlers];
      handler(handlerData);
      return;
    }
  }, [
    data.bundleCode,
    data.step?.action,
    data.step?.initialAmount?.fixed_amount,
    data.step?.productURL,
    hasActiveMainStep,
    mainStepAction,
    actionHandlers,
    promotionUnlockStep.cta.action,
  ]);

  const stepCode = getStepCode(mainStepAction, data.bundleCode, data.offers?.[0]?.code);

  const stickyPanelContent = (
    <OfferStickyPanelButtons
      shouldShowLoginButtons={showLoginButtons}
      offerValidFrom={bundleValidFrom}
      onStepAction={mainStepActionCallback}
      buttonLoading={null}
      currentAction={mainStepAction}
      currentButtonText={mainStepButtonText}
      currentButtonDisabled={promotionUnlockStep.step.disabled || false}
      currentStep={promotionUnlockStep.step.label}
      stepCode={stepCode}
      errorCode={data.assignationFailureReason}
      recurrenceNextAvailableDate={data.recurrenceNextAvailableDate}
    />
  );

  const shouldShowExpiration = shouldShowOfferExpirationDate(data.bundleStatus, data.offers);
  const expirationDate = shouldShowExpiration ? data.expirationDate : null;

  return (
    <div className={className} data-testid="multiple-offer" {...trackingAttributes}>
      <OfferHeader title={data.title} expirationDate={expirationDate} bundleStatus={data.bundleStatus} images={data.images} />

      <OfferContent
        offerBundleCode={data.bundleCode}
        steps={[promotionUnlockStep.step]}
        stickyPanelContent={stickyPanelContent}
        termsContent={data.termsAndConditions?.join('\n') ?? ''}
        termsTitle={termsTitle}
        bundleStatus={data.bundleStatus}
        namespace={'offers.page.obs.offer'}
        isMultipleOffers={true}
        offerGroups={offerGroups}
        showLoginButtons={showLoginButtons}
        hasActiveMainStep={hasActiveMainStep}
        offerActionHandlers={actionHandlers}
      />
    </div>
  );
}

export { MultipleOffer, type MultipleOfferProps };
